<script setup>
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons-vue";
import { getRequest, postRequest, putRequest } from "@/utils/request.js";
import { message } from "ant-design-vue";
import { reactive, ref, unref } from "vue";
import useUserStore from "@/store/userStore.js";
import { imageUrl as $imageUrl } from "@/utils/imageUrl.js";

const emit = defineEmits(["ok"]);
const title = ref("");
const open = ref(false);
const confirmLoading = ref(false);
const fileList = ref([]);
const loading = ref(false);
const imageUrl = ref("");
const userStore = useUserStore();
const token = userStore.getToken();
const formModel = ref({});
const formRef = ref();
const treeList = ref([]);
const rules = reactive({
  name: [
    {
      required: true,
      message: "请输入分类名称",
      trigger: "blur",
    },
  ],
  icon: [
    {
      required: true,
      message: "请上传图标",
      trigger: "blur",
    },
  ],
  jobTypeIds: [
    {
      required: true,
      message: "请选择关联工种",
      trigger: "blur",
    },
  ],
});

const handleAdd = (record) => {
  handleEdit(record);
};

const handleEdit = (record) => {
  formModel.value = Object.assign({}, { ...record });
  loadData();
  open.value = true;
};

const handleClose = () => {
  open.value = false;
  formRef.value.resetFields();
  imageUrl.value = "";
};

const handleConfirm = () => {
  formRef.value.validate().then(async () => {
    const unrefFormModel = unref(formModel);
    let request;
    if (unrefFormModel.id) {
      request = putRequest;
    } else {
      request = postRequest;
    }
    request("/jobType/category", unrefFormModel).then((response) => {
      if (response.success) {
        message.success("保存成功");
        emit("ok");
        handleClose();
      } else {
        message.error("保存失败。" + response.message);
      }
    });
  });
};

const loadData = () => {
  getRequest("/jobType").then((response) => {
    if (response.success) {
      response.data.forEach((bigCategory) => {
        bigCategory.checkable = false;
        bigCategory.children.forEach((smallCategory) => {
          smallCategory.checkable = false;
        });
      });
      treeList.value = response.data;
    }
  });
};

const typeChange = () => {
  rules.parentId[0].required = ["SMALL_CATEGORY", "JOB"].includes(
    formModel.value.type,
  );
};

function getBase64(img, callback) {
  const reader = new FileReader();
  reader.addEventListener("load", () => callback(reader.result));
  reader.readAsDataURL(img);
}

const handleChange = (info) => {
  if (info.file.status === "uploading") {
    loading.value = true;
    return;
  }
  if (info.file.status === "done") {
    // Get this url from response in real world.
    getBase64(info.file.originFileObj, (base64Url) => {
      imageUrl.value = base64Url;
      loading.value = false;
    });
    formModel.value.icon = info.file.response.data;
  }
  if (info.file.status === "error") {
    loading.value = false;
    message.error("upload error");
  }
};

const beforeUpload = (file) => {
  const regex = /^image/;
  const isJpgOrPng = regex.test(file.type);
  if (!isJpgOrPng) {
    message.error("只支持图片");
  }
  const isLt2M = file.size / 1024 / 1024 < 2;
  if (!isLt2M) {
    message.error("图片不能超过2m大小");
  }
  return isJpgOrPng && isLt2M;
};

const jobTypeIdsChange = (value, label, extra) => {
  formModel.value.jobTypeIds = value.toString();
};

defineExpose({
  handleAdd,
  handleEdit,
  handleClose,
  title,
});
</script>

<template>
  <a-modal
    width="800px"
    :open="open"
    :title="title"
    :confirm-loading="confirmLoading"
    @cancel="handleClose"
    @ok="handleConfirm"
  >
    <a-form
      ref="formRef"
      :labelCol="{ span: 3 }"
      :wrapperCol="{ span: 24 }"
      :model="formModel"
      :rules="rules"
    >
      <a-form-item label="名称" name="name">
        <a-input v-model:value="formModel.name" placeholder="请输入分类名称" />
      </a-form-item>
      <a-form-item label="图标" name="icon">
        <a-upload
          v-model:file-list="fileList"
          list-type="picture-card"
          class="avatar-uploader"
          :show-upload-list="false"
          :headers="{ Authorization: token }"
          :action="$uploadUrl + '?prefix=job/category/icon'"
          :before-upload="beforeUpload"
          @change="handleChange"
        >
          <a-image v-if="imageUrl" :src="imageUrl" :preview="false" />
          <div v-else>
            <loading-outlined v-if="loading"></loading-outlined>
            <plus-outlined v-else></plus-outlined>
            <div class="ant-upload-text">Upload</div>
          </div>
        </a-upload>
      </a-form-item>
      <a-form-item label="关联工种" name="jobTypeIds">
        <a-tree-select
          :treeData="treeList"
          v-model:value="formModel.selectedJobTypeIds"
          multiple
          tree-checkable
          :fieldNames="{
            children: 'children',
            label: 'name',
            value: 'id',
          }"
          placeholder="请选择上级菜单"
          @change="jobTypeIdsChange"
        ></a-tree-select>
      </a-form-item>
      <a-form-item label="排序" name="sort">
        <a-input-number
          v-model:value="formModel.sort"
          placeholder="请输入排序"
          style="width: 100%"
        />
      </a-form-item>
    </a-form>
  </a-modal>
</template>

<style scoped lang="scss"></style>
